import {
  type ApiErrorEventDetail,
  type GET_NETWORK_CONFIG_OPTION,
  type NETWORK_CONFIG_OPTION
} from './type';
import { getCommonHeaders, parseJSON } from './helper';
import {
  DELETE,
  PATCH,
  POST,
  PUT,
  GET,
  OPS_PORTAL,
  UNAUTHORIZED_MESSAGE,
  API_ERROR_EVENT,
  MAX_SCREEN_SHOT_SIZE_FOR_ERRORING_PAGE_IN_KB,
  CREATE_TICKET_API_URL
} from './constants';
import { isBrowser } from '../browser';
import { HTTP_STATUS_CODE } from '@dfg/brahma-types';
import { redirectTo } from '../document';
import { apiErrorManager } from '../apiErrorTracker';
import { captureScreenshot } from '../screenshot-utility';

export const request = async (url: string, options: any = {}) => {
  const headers = options?.headers || {};
  const traceId = headers['x-request-id'];
  const payload = options?.body;

  return await new Promise<any>((resolve, reject) => {
    if (!isBrowser()) {
      if (url.startsWith('/api')) {
        url = url.replace('/api', process.env.NEXT_PUBLIC_BFF_URL ?? '');
      } else if (url.startsWith('/kapi')) {
        url = url.replace('/kapi', process.env.NEXT_PUBLIC_KUBER_URL ?? '');
      } else if (url.startsWith('/wf-api')) {
        url = url.replace(
          '/wf-api',
          process.env.NEXT_PUBLIC_WORKFLOW_TOOLS_URL ?? ''
        );
      } else if (url.startsWith('/sc-api')) {
        url = url.replace(
          '/sc-api',
          process.env.NEXT_PUBLIC_SVAYAMCHALAK_URL ?? ''
        );
      }
    }

    fetch(url, { ...options })
      .then(async (response) => {
        const parsedResponse = await parseJSON(response);

        if (response.ok) {
          resolve(parsedResponse);
        }

        // redirect & reject with proper Error message
        if (
          (response as any)?.status === HTTP_STATUS_CODE.UNAUTHORIZED &&
          process.env.NEXT_PUBLIC_LOGIN_URL &&
          process.env.NEXT_PUBLIC_PORTAL === OPS_PORTAL
        ) {
          redirectTo(process.env.NEXT_PUBLIC_LOGIN_URL, '_blank');
          // TO DO: [Tech Debt] DT-9454 remove eslint-disable-next-line, when rejecting a promise
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            ...parsedResponse,
            error: {
              ...(parsedResponse?.error ?? {}),
              dfgMessage: UNAUTHORIZED_MESSAGE
            }
          });
        }
        if (parsedResponse?.error) {
          apiErrorManager.trackFailedApi(url, parsedResponse, traceId, payload);

          if (typeof window !== 'undefined' && url !== CREATE_TICKET_API_URL) {
            const pageURL = window.location.href;
            const eventDetail: ApiErrorEventDetail = {
              error: parsedResponse.error,
              url,
              pageURL,
              portal: process.env.NEXT_PUBLIC_PORTAL ?? '',
              traceId: traceId ?? null,
              screenshot: null
            };
            const elementToCapture =
              typeof document !== 'undefined' ? document.body : null;
            if (elementToCapture) {
              captureScreenshot(
                elementToCapture,
                MAX_SCREEN_SHOT_SIZE_FOR_ERRORING_PAGE_IN_KB
              )
                .then((data) => {
                  if (data) {
                    const customEvent = new CustomEvent(API_ERROR_EVENT, {
                      detail: { ...eventDetail, screenshot: data }
                    });
                    window.dispatchEvent(customEvent);
                    return;
                  }
                  const customEvent = new CustomEvent(API_ERROR_EVENT, {
                    detail: eventDetail
                  });
                  window.dispatchEvent(customEvent);
                })
                .catch(() => {
                  const customEvent = new CustomEvent(API_ERROR_EVENT, {
                    detail: eventDetail
                  });
                  window.dispatchEvent(customEvent);
                });
            } else {
              const customEvent = new CustomEvent(API_ERROR_EVENT, {
                detail: eventDetail
              });
              window.dispatchEvent(customEvent);
            }
          }
        }
        reject(parsedResponse);
      })
      .catch((error) => {
        apiErrorManager.trackFailedApi(url, error, traceId, payload);

        // redirect & reject with proper Error message
        if (
          error?.error?.httpStatusCode === HTTP_STATUS_CODE.UNAUTHORIZED &&
          process.env.NEXT_PUBLIC_LOGIN_URL
        ) {
          redirectTo(process.env.NEXT_PUBLIC_LOGIN_URL, '_blank');
          // TO DO: [Tech Debt] DT-9454 remove eslint-disable-next-line, when rejecting a promise
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            ...error,
            error: {
              ...(error?.error ?? {}),
              dfgMessage: UNAUTHORIZED_MESSAGE
            }
          });
        }
        reject(error);
      });
  });
};

export const deleteReq = async (
  url: string,
  options: NETWORK_CONFIG_OPTION
) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: DELETE,
    credentials: 'include'
  });
};

export const get = async (
  url: string,
  options: GET_NETWORK_CONFIG_OPTION = {}
) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: GET,
    credentials: 'include'
  });
};

export const patch = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: PATCH,
    credentials: 'include'
  });
};

export const post = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: POST,
    credentials: 'include'
  });
};

export const put = async (url: string, options: NETWORK_CONFIG_OPTION) => {
  return await request(url, {
    ...options,
    headers: { ...getCommonHeaders(), ...options.headers },
    method: PUT,
    credentials: 'include'
  });
};
